import { useMemo, useState } from "react";
import type { ITranscription } from "./useTranscript";
import type { IScriptTemplate } from "@shadowcue/ui";

export type SectionFilterValue = number | "offScript" | null;

interface UseTranscriptFiltersResult {
  speakerFilter: number | null;
  setSpeakerFilter: (value: number | null) => void;
  sectionFilter: SectionFilterValue;
  setSectionFilter: (value: SectionFilterValue) => void;
  filteredLines: ITranscription[];
  availableSpeakers: number[];
  clearFilters: () => void;
  hasAnyFilter: boolean;
}

const useTranscriptFilters = (
  lines: ITranscription[],
  template: IScriptTemplate | null,
): UseTranscriptFiltersResult => {
  const [speakerFilter, setSpeakerFilter] = useState<number | null>(null);
  const [sectionFilter, setSectionFilter] = useState<SectionFilterValue>(null);

  const availableSpeakers = useMemo(() => {
    const speakers = new Set<number>();
    lines.forEach((line) => speakers.add(line.speaker));
    return Array.from(speakers).sort((a, b) => a - b);
  }, [lines]);

  // Map each question id to the section it belongs to, so a line (which only
  // carries a scriptTemplateQuestionId) can be matched against a selected
  // section.
  const questionIdToSectionId = useMemo(() => {
    const map = new Map<number, number>();
    template?.scriptTemplateSections.forEach((section) => {
      if (section.id === undefined) return;
      const sectionId = section.id;
      section.scriptTemplateQuestions.forEach((question) => {
        if (question.id !== undefined) map.set(question.id, sectionId);
      });
    });
    return map;
  }, [template]);

  const filteredLines = useMemo(() => {
    return lines.filter((line) => {
      if (speakerFilter !== null && line.speaker !== speakerFilter) {
        return false;
      }
      if (sectionFilter === "offScript") {
        return line.scriptTemplateQuestionId == null;
      }
      if (sectionFilter !== null) {
        return (
          line.scriptTemplateQuestionId != null &&
          questionIdToSectionId.get(line.scriptTemplateQuestionId) ===
            sectionFilter
        );
      }
      return true;
    });
  }, [lines, speakerFilter, sectionFilter, questionIdToSectionId]);

  const clearFilters = () => {
    setSpeakerFilter(null);
    setSectionFilter(null);
  };

  const hasAnyFilter = speakerFilter !== null || sectionFilter !== null;

  return {
    speakerFilter,
    setSpeakerFilter,
    sectionFilter,
    setSectionFilter,
    filteredLines,
    availableSpeakers,
    clearFilters,
    hasAnyFilter,
  };
};

export default useTranscriptFilters;
